import { CommonModule } from "@angular/common";
import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import {
  ActivatedRoute,
  Router,
  RouterEvent,
  RouterModule,
  RouterState,
} from "@angular/router";
import { TranslateService } from "@ngx-translate/core";

import { WhiteLabelService } from "app/core/white-label/white-label.service";

import {
  LoaderComponent,
  OverlayContainerComponent,
  TestToolComponent,
  UserSettingsDrawerComponent,
  UserSettingsDrawerService,
} from "@shared";

import { environment } from "environments/environment";
import { Observable, Subject } from "rxjs";
import { takeUntil, window } from "rxjs/operators";
import { RouterEventService } from "./core/services/router-event.service";
import { ThemeService } from "./core/theme/theme.service";

@Component({
  selector: "app-main",
  template: `
    @if (testToolAvailable) {
      <test-tool></test-tool>
    }

    <router-outlet></router-outlet>

    @if (isUserSettingsShown$ | async) {
      <user-settings-drawer
        (click)="toggleUserSettings()"
        (closeUserSettingsDrawer)="toggleUserSettings()"
      ></user-settings-drawer>
    }

    <overlay-container></overlay-container>
    <loader></loader>
  `,
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    LoaderComponent,
    OverlayContainerComponent,
    TestToolComponent,
    UserSettingsDrawerComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private userSettingsDrawerService = inject(UserSettingsDrawerService);
  private routerEventService = inject(RouterEventService);
  private titleService = inject(Title);
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private whiteLabelService = inject(WhiteLabelService);
  private themeService = inject(ThemeService);

  public testToolAvailable: boolean =
    environment.testToolEnable && !window["e2e"];
  public isUserSettingsShown$: Observable<boolean>;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private translationParams: any = {};

  constructor() {
    this.routerEventService
      .listenForRouteChanges()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((routerEvent: RouterEvent) => {
        this.setTabTitle();
        this.routerEventService.updateRouteChangedSource(routerEvent.url);
      });
  }

  private setTabTitle(): void {
    const title: string = this.getTitle(
      this.router.routerState,
      this.router.routerState.root,
    ).join("-");

    if (title && title.trim() !== "") {
      this.translateService
        .get(title, this.translationParams)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((tabTitle: string) => {
          const themeTabTitle: string = this.getThemeTabTitle(tabTitle);
          this.titleService.setTitle(themeTabTitle);
        });
    }
  }

  private getThemeTabTitle(tabTitle: string): string {
    const themeLabel: string = this.themeService.getThemeData().label;
    return `${themeLabel}: ${tabTitle}`;
  }

  /**
   * Shows content
   */
  private getTitle(state: RouterState, parent: ActivatedRoute): string[] {
    const data: string[] = [];

    if (parent && parent.snapshot.data && parent.snapshot.data.title) {
      data.push(parent.snapshot.data.title);

      if (parent.snapshot.data.whitelabelTranslationParams) {
        const translationParamsKey: string =
          parent.snapshot.data.whitelabelTranslationParams;
        this.translationParams[translationParamsKey] =
          this.whiteLabelService.data[translationParamsKey];
      } else {
        this.translationParams = {};
      }
    }

    if (state && parent) {
      // @ts-expect-error: firstChild error while in prototype but not in RouterState definition
      data.push(...this.getTitle(state, state.firstChild(parent)));
    }
    return data;
  }

  public ngOnInit(): void {
    this.isUserSettingsShown$ =
      this.userSettingsDrawerService.isUserSettingsShown$;
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public toggleUserSettings(): void {
    this.userSettingsDrawerService.toggle();
  }
}
